import React, { useContext, useEffect, useMemo } from "react";
import TimelinePage from "features/signals/SignalDrawer/Timeline/TimelinePage";
import {
  ADMIN_SIGNALS_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
  SMALL_SCREEN,
} from "common/constants";
import { invert } from "lodash";
import { useRouteMatch } from "react-router-dom";
import { selectSignalGeneralInfo } from "@shared/slices/signalExtraDetails.slice";
import TargetsPage from "features/signals/SignalDrawer/Targets/TargetsPage";
import GeneralInfo from "features/signals/SignalDrawer/GeneralInfo.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import DrawerTitle from "features/signals/SignalDrawer/Title/DrawerTitle";
import {
  getRouteWithoutPath,
  getSignalExtraDetailsRoute,
  useQueryGroupId,
} from "utils/routes";
import EditSignalWrapper from "features/signals/EditSignal/EditSignalWrapper";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { useTheme } from "styled-components";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { WebsiteOriginTimeOfAction } from "analytics/events";
import { useSelector } from "react-redux";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import FollowSignalProvider from "@shared/features/signals/FollowSignalProvider";
import SignalFollowModal from "features/signals/SignalFollow/SignalFollowModal";
import SignalDetailsProvider from "@shared/features/signals/common/SignalDetailsProvider";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import {
  SIGNAL_EXTRA_DETAILS_TABS,
  SIGNAL_EXTRA_DETAILS_TABS_TO_DISPLAY,
} from "@shared/common/constants";
import { ValueOf } from "@shared/types/utils";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import useMedia from "utils/hooks/useMedia";
import SignalChart from "features/signals/SignalDrawer/SignalChart";
import {
  useBottomNavigator,
  useDrawerDelayedUnmount,
} from "utils/hooks/misc.tsx";
import DrawerTradesPage from "features/trades/common/DrawerTradesPage.tsx";
import { FilterType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";

const SignalDrawer = () => {
  const isAdminSignals = Boolean(useRouteMatch(ADMIN_SIGNALS_PATH));
  const extraDetailsMatch = useRouteMatch(SIGNAL_EXTRA_DETAILS_PATH);
  const {
    signalId: signalIdStr,
    tab,
    edit,
  }: Record<string, string> = (extraDetailsMatch && extraDetailsMatch.params) ||
  {};
  const displayTradeDetails = useDisplayTradeDetails();
  const isEditUrl = edit === "edit";
  const signalId = displayTradeDetails
    ? signalIdStr
      ? parseInt(signalIdStr)
      : undefined
    : undefined;
  const querySubGroupId = useQueryGroupId();
  const { id: drawerSignalId } = useDrawerDelayedUnmount(signalId);
  const { isFetching: isFetchingLiveSearch } =
    sharedLib.sharedApi.configuration.endpoints.liveUpdateChannels.useQueryState(
      querySubGroupId ? { sub_group_id: querySubGroupId } : skipToken
    );
  const drawerOpen = !!signalId && !isFetchingLiveSearch;

  return (
    <SignalDetailsProvider
      signalId={isFetchingLiveSearch ? undefined : drawerSignalId}
      forAdmin={isAdminSignals}
      isEditUrl={isEditUrl}
      tab={tab as ValueOf<typeof SIGNAL_EXTRA_DETAILS_TABS> | undefined}
    >
      <FollowSignalProvider SignalFollowModal={SignalFollowModal}>
        <EditSignalWrapper>
          <SignalDrawerContent drawerOpen={drawerOpen} />
        </EditSignalWrapper>
      </FollowSignalProvider>
    </SignalDetailsProvider>
  );
};

export default SignalDrawer;

interface SignalDrawerContentProps {
  drawerOpen: boolean;
}

const SignalDrawerContent = ({ drawerOpen }: SignalDrawerContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const {
    isEdit,
    signalId,
    tab,
    exchangeSignalArgs,
    getMostFulfilledSignalForGroupQuery,
    configurationQuery,
    getPriceGraphDataQuery,
    getSignalExtraInfoTargetsQuery,
    getExchangeSignalIdsQuery,
    selectedExchange,
    setSelectedExchange,
    signalExchanges,
    forAdmin,
    generalDataQuery,
    getSignalExtraInfoActivitiesQuery,
  } = useContext(SignalDetailsContext)!;
  const editSignalContext = useContext(EditSignalContext);
  const appTheme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const { analytics } = useContext(AnalyticsContext);
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const {
    data: mostFulfilledExchangeSignalId,
    isFetching: mostFulfilledIsFetching,
  } = getMostFulfilledSignalForGroupQuery;
  const { data: signalExchangesMap } = getExchangeSignalIdsQuery;

  const signalGeneralData = useSelector(
    selectSignalGeneralInfo(exchangeSignalArgs)
  );
  const subGroupID = signalGeneralData?.sub_group_id;
  const symbol = signalGeneralData?.symbol;

  useEffect(() => {
    if (subGroupID && signalId) {
      analytics?.signalDetailsViewClicked({
        groupSignalId: signalId,
        groupChannelId: subGroupID,
        websiteOriginTimeOfAction: forAdmin
          ? WebsiteOriginTimeOfAction.ADMIN_PANEL
          : WebsiteOriginTimeOfAction.SIGNALS,
      });
    }
  }, [subGroupID, signalId]);

  useEffect(() => {
    if (
      signalExchangesMap &&
      mostFulfilledExchangeSignalId &&
      !mostFulfilledIsFetching
    ) {
      const mostFulfilledExchange =
        invert(signalExchangesMap)[
          mostFulfilledExchangeSignalId.exchange_signal_id
        ];

      if (isEdit && tab === SIGNAL_EXTRA_DETAILS_TABS.TARGETS) {
        setSelectedExchange(mostFulfilledExchange);
        if (mostFulfilledExchange === selectedExchange) {
          getPriceGraphDataQuery.refetch();
          getSignalExtraInfoTargetsQuery.refetch();
          return;
        }
      }
    }
  }, [mostFulfilledIsFetching, signalExchangesMap]);

  const onEditModeClicked = (
    isEditMode: boolean,
    nextTab?: ValueOf<typeof SIGNAL_EXTRA_DETAILS_TABS>
  ) => {
    if (nextTab) {
      onTabChange(nextTab, isEditMode);
    } else if (tab === SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION) {
      onTabChange(tab, isEditMode);
    } else {
      onTabChange(SIGNAL_EXTRA_DETAILS_TABS.TARGETS, isEditMode);
    }
  };

  const resetChanges = (
    onCancelChangesClick?: (callback: () => void) => void,
    closeDrawerCallback?: () => void
  ) => {
    onCancelChangesClick?.(() => {
      if (closeDrawerCallback) closeDrawerCallback();
      else onEditModeClicked(false);
    });
  };

  const onCloseClicked = () => {
    resetChanges(editSignalContext?.onCancelChangesClick);
    return true;
  };

  const onTabChange = (tab: string, isEdit: boolean) => {
    const baseRoute = getRouteWithoutPath(
      location.pathname,
      SIGNAL_EXTRA_DETAILS_PATH
    );
    if (signalId) {
      historyPush(getSignalExtraDetailsRoute(baseRoute, signalId, tab, isEdit));
    }
  };

  const tradesArgs = useMemo(() => ({ signal_id: signalId }), [signalId]);

  const getTabsToComponent = () => {
    const tabs = {
      [SIGNAL_EXTRA_DETAILS_TABS.GENERAL]: <GeneralInfo />,
      [SIGNAL_EXTRA_DETAILS_TABS.TIMELINE]: (
        <TimelinePage exchanges={signalExchanges} symbol={symbol} />
      ),
      [SIGNAL_EXTRA_DETAILS_TABS.TARGETS]: (
        <TargetsPage
          exchanges={signalExchanges}
          signalExchangeArgs={getSignalExtraInfoTargetsQuery.originalArgs}
          symbol={symbol}
        />
      ),
      [SIGNAL_EXTRA_DETAILS_TABS.TRADES]: (
        <DrawerTradesPage
          baseGetTradesArgs={tradesArgs}
          hideFilterTypes={TRADES_HIDE_FILTER_TYPES}
          tabKey={SIGNAL_EXTRA_DETAILS_TABS.TRADES}
        />
      ),
    };

    if (editSignalContext) {
      const { generalConfiguration, editSignalConfigurationLoading } =
        editSignalContext;
      tabs[SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION] = (
        <LoadingSpinnerOverlay
          isLoading={
            configurationQuery.isLoading || editSignalConfigurationLoading
          }
          spinnerMarginTop={50}
          hideContentWhileLoading={false}
          wrap={true}
          backgroundColor={appTheme.colors.cardBgColor}
        >
          {generalConfiguration}
        </LoadingSpinnerOverlay>
      );
    }
    return tabs;
  };

  const commonQueries = [
    getPriceGraphDataQuery,
    getSignalExtraInfoActivitiesQuery,
    getSignalExtraInfoTargetsQuery,
  ];

  const tabToQueries = {
    [SIGNAL_EXTRA_DETAILS_TABS.GENERAL]: [generalDataQuery],
    [SIGNAL_EXTRA_DETAILS_TABS.TIMELINE]: commonQueries,
    [SIGNAL_EXTRA_DETAILS_TABS.TARGETS]: commonQueries,
    [SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION]: [configurationQuery],
  };

  return (
    <ExtraDetailsDrawer
      defaultTab={tab || SIGNAL_EXTRA_DETAILS_TABS.GENERAL}
      hideTabs={isEdit}
      hideCloseButton={isEdit}
      tab={tab}
      path={SIGNAL_EXTRA_DETAILS_PATH}
      visible={drawerOpen}
      tabsToComponents={getTabsToComponent()}
      onTabChange={(newTab) => onTabChange(newTab, isEdit)}
      onCloseDrawer={(callback) =>
        resetChanges(editSignalContext?.onCancelChangesClick, callback)
      }
      title={
        <DrawerTitle
          onEditModeClicked={(
            tab?: ValueOf<typeof SIGNAL_EXTRA_DETAILS_TABS>
          ) => onEditModeClicked(true, tab)}
          onCloseClicked={onCloseClicked}
        />
      }
      tabToQueries={isEdit ? undefined : tabToQueries}
      extension={
        isSmallScreen || !generalDataQuery.data ? undefined : (
          <SignalChart
            symbol={symbol}
            exchanges={signalExchanges}
            isExtension={true}
          />
        )
      }
      innerContentWrapperStyle={{ paddingBottom: isBottomNavigator ? 50 : 0 }}
      flatListTabs={[SIGNAL_EXTRA_DETAILS_TABS.TRADES]}
      getTabLabel={(tab) => t(SIGNAL_EXTRA_DETAILS_TABS_TO_DISPLAY[tab])}
    />
  );
};

const TRADES_HIDE_FILTER_TYPES = [
  FilterType.SOURCE_TYPE,
  FilterType.SOURCE_NAME,
];
